import React, { useMemo, useState } from 'react';
import { SpaceBetween, Spinner, Alert, Box, Table, Select, SelectProps, Popover } from '@amzn/awsui-components-core';
import { useConnectionsFreetextResponses } from './useConnectionsData';
import { ConnectionsFreetextResponse, ResponseSentiment } from './types';
import './freetext-responses-card.scss';

interface FreetextResponsesCardProps {
  leaderLogin: string;
  metricPeriod: string;
  startDate?: string;
  leaderName: string;
  periodLabel: string;
}

type SentFilter = 'ALL' | ResponseSentiment;

interface Token {
  word: string;
  count: number;
  pos: number;
  neg: number;
}

// Common English + survey-boilerplate stopwords. Kept inline (no dep) so the cloud
// surfaces meaningful terms rather than "the/and/to".
const STOPWORDS = new Set(
  (
    'the a an and or but if then else of to in on at for with without by from up down out over under ' +
    'is am are was were be been being do does did doing have has had having i me my we our us you your ' +
    'they them their he she it its this that these those as so than too very can will would should could ' +
    'not no yes more most much many lot lots also just about into which who whom what when where why how ' +
    'am pm etc eg ie amazon work working job team teams'
  ).split(/\s+/)
);

const tokenize = (text: string): string[] => (text.toLowerCase().match(/[a-z']{3,}/g) || []).filter((w) => !STOPWORDS.has(w));

const sentLabel = (s: ResponseSentiment | null): string => (s === 'POSITIVE' ? 'Positive' : s === 'NEGATIVE' ? 'Negative' : 'Unclassified');
const sentClass = (s: ResponseSentiment | null): string =>
  s === 'POSITIVE' ? 'ftr-sentiment--pos' : s === 'NEGATIVE' ? 'ftr-sentiment--neg' : 'ftr-sentiment--neu';

const TOPIC_ALL: SelectProps.Option = { label: 'All Topics', value: 'ALL' };

// Long free-text cell: the row shows a short single-line PREVIEW; clicking it opens a
// popover with the complete text (scrollable for very long verbatims). Short values render
// inline without a popover.
const PREVIEW_LEN = 60;
const longTextCell = (text: string | null): React.ReactNode => {
  if (!text) return '-';
  if (text.length <= PREVIEW_LEN) return text;
  return (
    <Popover
      dismissButton={false}
      position="top"
      size="large"
      triggerType="text"
      renderWithPortal
      content={<div className="ftr-popover-content">{text}</div>}
    >
      <span className="ftr-preview">{text}</span>
    </Popover>
  );
};

export const FreetextResponsesCard: React.FC<FreetextResponsesCardProps> = ({ leaderLogin, metricPeriod, startDate, leaderName, periodLabel }) => {
  const state = useConnectionsFreetextResponses(leaderLogin, metricPeriod, startDate);
  const [sentFilter, setSentFilter] = useState<SentFilter>('ALL');
  const [topicFilter, setTopicFilter] = useState<string>('ALL');
  const [selectedWord, setSelectedWord] = useState<string | null>(null);

  const responses = useMemo(() => state.data || [], [state.data]);

  // Distinct topics across ALL responses (stable regardless of the sentiment filter),
  // so the topic dropdown options don't shift when sentiment changes.
  const allTopics = useMemo(() => {
    const s = new Set<string>();
    responses.forEach((r) => {
      if (r.topic) s.add(r.topic);
    });
    return Array.from(s).sort();
  }, [responses]);

  const topicOptions: SelectProps.Option[] = useMemo(() => [TOPIC_ALL, ...allTopics.map((t) => ({ label: t, value: t }))], [allTopics]);
  const selectedTopicOption = topicOptions.find((o) => o.value === topicFilter) || TOPIC_ALL;

  // Apply BOTH sentiment and topic filters -> drives the word cloud and the table.
  const filtered = useMemo(
    () =>
      responses.filter(
        (r) => (sentFilter === 'ALL' || (r.sentiment || 'Unknown') === sentFilter) && (topicFilter === 'ALL' || r.topic === topicFilter)
      ),
    [responses, sentFilter, topicFilter]
  );

  // Token frequency with sentiment split. Count each distinct word once per response
  // so a single long comment doesn't dominate the cloud.
  const tokens: Token[] = useMemo(() => {
    const map = new Map<string, Token>();
    filtered.forEach((r) => {
      const uniq = new Set(tokenize(r.writtenResponse));
      uniq.forEach((w) => {
        const t = map.get(w) || { word: w, count: 0, pos: 0, neg: 0 };
        t.count += 1;
        if (r.sentiment === 'POSITIVE') t.pos += 1;
        else if (r.sentiment === 'NEGATIVE') t.neg += 1;
        map.set(w, t);
      });
    });
    return Array.from(map.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 60);
  }, [filtered]);

  const maxCount = tokens.length ? tokens[0].count : 1;
  // sqrt scale so frequent words don't overwhelm; 12px..34px.
  const sizeFor = (c: number) => 12 + Math.round(22 * Math.sqrt(c / maxCount));
  const wordClass = (t: Token) => (t.pos > t.neg ? 'ftr-word--pos' : t.neg > t.pos ? 'ftr-word--neg' : 'ftr-word--neu');

  // The responses table stays hidden until the user engages a filter -- a sentiment
  // chip, the topic dropdown, or a word click -- so we don't dump all responses by default.
  const showTable = selectedWord !== null || sentFilter !== 'ALL' || topicFilter !== 'ALL';

  // Drill-down list: responses matching the clicked word (within the active filters).
  const drill = useMemo(() => {
    if (!selectedWord) return filtered;
    return filtered.filter((r) => tokenize(r.writtenResponse).includes(selectedWord));
  }, [filtered, selectedWord]);

  const sentimentFilters: SentFilter[] = ['ALL', 'POSITIVE', 'NEGATIVE', 'Unknown'];
  const filterLabel = (s: SentFilter) => (s === 'ALL' ? 'All' : sentLabel(s as ResponseSentiment));

  if (state.loading) {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="normal" /> Loading verbatim responses...
      </Box>
    );
  }
  if (state.error) {
    return (
      <Alert type="error" header="Failed to load verbatim responses">
        {state.error.message}
      </Alert>
    );
  }
  if (responses.length === 0) {
    return (
      <Alert type="info" header="No verbatim responses">
        No raw free-text responses are available for {leaderName} in {periodLabel}.
      </Alert>
    );
  }

  return (
    <SpaceBetween size="m">
      <Box variant="small" color="text-body-secondary">
        {leaderName}, {periodLabel} · {responses.length} responses
      </Box>
      <div className="ftr-filters" role="group" aria-label="Freetext filters">
        {sentimentFilters.map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={sentFilter === s}
            className={`ftr-chip ${sentFilter === s ? 'ftr-chip--active' : ''}`}
            onClick={() => {
              setSentFilter(s);
              setSelectedWord(null);
            }}
          >
            {filterLabel(s)}
          </button>
        ))}
        <div className="ftr-topic-select">
          <Select
            selectedOption={selectedTopicOption}
            onChange={({ detail }) => {
              setTopicFilter(detail.selectedOption.value || 'ALL');
              setSelectedWord(null);
            }}
            options={topicOptions}
            expandToViewport
            filteringType="auto"
            ariaLabel="Topic filter"
          />
        </div>
      </div>

      {tokens.length === 0 ? (
        <Box color="text-status-inactive">No word-cloud terms for this filter.</Box>
      ) : (
        <div className="ftr-cloud">
          {tokens.map((t) => (
            <button
              key={t.word}
              type="button"
              className={`ftr-word ${wordClass(t)} ${selectedWord === t.word ? 'ftr-word--sel' : ''}`}
              style={{ fontSize: `${sizeFor(t.count)}px` }}
              title={`${t.count} response(s)`}
              onClick={() => setSelectedWord(selectedWord === t.word ? null : t.word)}
            >
              {t.word}
            </button>
          ))}
        </div>
      )}

      {showTable ? (
        <Box>
          {selectedWord && (
            <Box variant="small" padding={{ bottom: 'xs' }}>
              Showing {drill.length} response(s) containing &ldquo;<strong>{selectedWord}</strong>&rdquo; -{' '}
              <button type="button" className="ftr-clear" onClick={() => setSelectedWord(null)}>
                clear
              </button>
            </Box>
          )}
          <Table
            columnDefinitions={[
              {
                id: 'sentiment',
                header: 'Sentiment',
                cell: (r: ConnectionsFreetextResponse) => <span className={sentClass(r.sentiment)}>{sentLabel(r.sentiment)}</span>,
                width: 140,
                minWidth: 120
              },
              {
                id: 'topic',
                header: 'Topic',
                cell: (r: ConnectionsFreetextResponse) => r.topic || '-',
                width: 260,
                minWidth: 200
              },
              {
                id: 'question',
                header: 'Question',
                cell: (r: ConnectionsFreetextResponse) => longTextCell(r.questionText),
                width: 400,
                minWidth: 240
              },
              {
                id: 'response',
                header: 'Response',
                cell: (r: ConnectionsFreetextResponse) => longTextCell(r.writtenResponse),
                minWidth: 460
              }
            ]}
            items={drill}
            variant="embedded"
            resizableColumns
            empty={<Box textAlign="center">No responses</Box>}
          />
        </Box>
      ) : (
        <Box color="text-status-inactive" textAlign="center" padding={{ vertical: 'xs' }}>
          Click a word, or pick a sentiment or topic above, to see the underlying responses.
        </Box>
      )}
    </SpaceBetween>
  );
};
